// components/dashboards/Governanca/cppd/CPPDTimelineReuniao.tsx
import React from 'react';
import { CheckCircleFilled } from '@ant-design/icons';

interface Reuniao {
  id: number;
  nome: string;
  data: string;
  status: 'Concluído' | 'Em andamento' | 'Pendente';
}

interface CPPDTimelineReuniaoProps {
  reunioes: Reuniao[];
}

export const CPPDTimelineReuniao: React.FC<CPPDTimelineReuniaoProps> = ({ reunioes }) => {
  // Função para obter a cor do status
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Concluído':
        return '#16B364'; // Verde
      case 'Em andamento':
        return '#2E90FA'; // Azul
      case 'Pendente':
        return '#F79009'; // Amarelo
      default:
        return '#D0D5DD'; // Cinza
    }
  };

  // Função para formatar o nome como "R1", "R2", etc.
  const formatCode = (index: number) => {
    return `R${index + 1}`;
  };

  // Função para obter texto do status para exibição
  const getStatusText = (status: string) => {
    switch(status) {
      case 'Concluído':
        return 'Realizada';
      default:
        return status;
    }
  };

  // Função para separar data e hora
  const separateDateAndTime = (dateString: string) => {
    // Verifica se a string tem o formato "DD/MM/YY HH:MM"
    const dateParts = dateString.split(' ');
    if (dateParts.length === 2) {
      return { date: dateParts[0], time: dateParts[1] };
    }
    
    // Se não tem o formato esperado, retorna a string original como data
    return { date: dateString, time: '10:00' };
  };

  // Ordena as reuniões 
  const sortedReunioes = React.useMemo(() => {
    return [...reunioes].sort((a, b) => {
      // Primeiro por status (Concluídas primeiro, depois Em andamento, depois Pendentes)
      const statusOrder: Record<string, number> = { 'Concluído': 0, 'Em andamento': 1, 'Pendente': 2 };
      const statusA = statusOrder[a.status] || 3;
      const statusB = statusOrder[b.status] || 3;
      
      if (statusA !== statusB) return statusA - statusB;
      
      // Depois por data
      return a.data.localeCompare(b.data);
    });
  }, [reunioes]);

  return (
    <div className="timeline-container" style={{ display: 'flex', overflowX: 'auto', padding: '8px 0 20px' }}>
      {sortedReunioes.map((reuniao, index) => {
        const statusColor = getStatusColor(reuniao.status);
        const { date, time } = separateDateAndTime(reuniao.data);
        const isCompleted = reuniao.status === 'Concluído';
        const isActive = reuniao.status === 'Em andamento';
        const reuniaoCode = formatCode(index);
        
        // CORREÇÃO SIMPLES: Key única usando index + ID + substring do nome
        const uniqueKey = `timeline-${index}-${reuniao.id}-${reuniao.nome.replace(/\s+/g, '').substring(0, 5)}`;
        
        return (
          <div 
            key={uniqueKey} // Key única garantida
            className="timeline-item" 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              minWidth: '140px',
              position: 'relative',
              marginRight: '10px'
            }}
          >
            {/* Ponto da timeline */}
            <div 
              className="timeline-point" 
              style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                backgroundColor: isCompleted ? statusColor : isActive ? statusColor : '#EAECF0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
                color: isCompleted || isActive ? 'white' : '#667085',
                fontWeight: 'bold',
                fontSize: '12px'
              }}
            >
              {index + 1}
            </div>
            
            {/* Linha da timeline */}
            {index < sortedReunioes.length - 1 && (
              <div 
                className="timeline-line" 
                style={{ 
                  position: 'absolute',
                  top: '12px',
                  left: '24px',
                  height: '2px',
                  width: 'calc(100% + 10px)',
                  backgroundColor: '#EAECF0',
                  zIndex: 1
                }}
              ></div>
            )}
            
            {/* Conteúdo */}
            <div 
              className="timeline-content" 
              style={{ 
                textAlign: 'center',
                marginTop: '10px',
                width: '100%'
              }}
            >
              {/* Código da Reunião */}
              <div 
                className="timeline-code" 
                style={{ 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#101828',
                  marginBottom: '4px'
                }}
              >
                {reuniaoCode}
              </div>
              
              {/* Nome da Reunião */}
              <div 
                className="timeline-title" 
                style={{ 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#101828',
                  marginBottom: '4px'
                }}
              >
                {reuniao.nome}
              </div>
              
              {/* Data */}
              <div 
                className="timeline-date" 
                style={{ 
                  fontSize: '12px',
                  color: '#667085',
                  marginBottom: '2px'
                }}
              >
                {date}
              </div>
              
              {/* Hora */}
              <div 
                className="timeline-time" 
                style={{ 
                  fontSize: '12px',
                  color: '#667085',
                  marginBottom: '4px'
                }}
              >
                {time}
              </div>
              
              {/* Status */}
              <div 
                className="timeline-status" 
                style={{ 
                  fontSize: '12px',
                  color: statusColor,
                  fontWeight: 'medium',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  display: 'inline-block'
                }}
              >
                {getStatusText(reuniao.status)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CPPDTimelineReuniao;